import {
  type AsyncComponentLoader,
  type Component,
  type ComponentPublicInstance,
  defineAsyncComponent,
  markRaw,
} from "vue";
import AsyncLoadError from "../components/async/AsyncLoadError.vue";
import AsyncLoader from "../components/async/AsyncLoader.vue";
import { type ConfigSection, type GameProfile, type Menu, MenuItem, type ProfileCtx } from "./types";

export function asyncRoute<
  T extends Component = {
    new (): ComponentPublicInstance;
  },
>(source: AsyncComponentLoader<T>) {
  return markRaw(
    defineAsyncComponent({
      loader: source,
      loadingComponent: AsyncLoader,
      errorComponent: AsyncLoadError,
      delay: 50,
      timeout: 60000,
      // I wish the following worked, but alas, it does not. Browsers (correctly, according to spec) cache failed
      // dynamic imports, so even after restoring network connection, the promise will still return the original, first result.
      // Bully the commission here if you want this to work without a major painful workaround to not use native dynamic imports that is not worth it:
      // https://github.com/whatwg/html/issues/6768
      // Another solution would be "cache-busting" URLs for the import, you can bully vite developers about that here:
      // https://github.com/vitejs/vite/issues/5101
      /* onError: (error, retry, fail, attempts) => {
                        let delay = Math.min(100000, attempts * 10000); // Delay based on attempts, capped at 60 seconds
                        console.error("Failed to load async component:", error, ", retrying in", delay / 1000, "s");
                        setTimeout(retry, delay);
                        fail();
                    } */
    }),
  );
}

import { faCloudArrowUp, faKey, faPowerOff } from "@fortawesome/free-solid-svg-icons";
import { commonMenuItems } from "./common";

const createMenu = (): Menu => [
  {
    name: "settings",
    items: [commonMenuItems.status, commonMenuItems.settings, commonMenuItems.fileManager],
  },
  {
    name: "administration",
    items: [
      /* new MenuItem(
                            'Auto Update',
                            'fa-file-upload', 
                        ), */
      new MenuItem(
        "restarts",
        faPowerOff,
        asyncRoute(() => import("../components/views/Restarts.vue")),
      ),
      new MenuItem(
        "backups",
        faCloudArrowUp,
        asyncRoute(() => import("../components/views/Backups.vue")),
      ),
      new MenuItem(
        "permissions",
        faKey,
        asyncRoute(() => import("../components/views/Permissions.vue")),
      ),
    ],
  },
];

export function getOrderedConfDefSections(ctx?: ProfileCtx) {
  if (ctx?.profile?.configDefinition) {
    const confDefSections = Object.entries(ctx.profile.configDefinition.sections);
    confDefSections.sort((a, b) => a[1].order - b[1].order);
    return confDefSections;
  }
  return [];
}

export function buildConfigSections(ctx?: ProfileCtx): ConfigSection[] {
  const orderedConfDefSections = getOrderedConfDefSections(ctx);
  const sections = [];
  for (const [name, _] of orderedConfDefSections) {
    sections.push({ component: "ConfSection", props: { name } });
  }
  return sections;
}

export default (ctx?: ProfileCtx): GameProfile => ({
  menu: createMenu(),
  configSections: buildConfigSections(ctx), // Automatically build frontend config sections by default
  isBeta: false,
  hasDns: false,
  hasExpertMode: false,
  hasActivePlayers: true,
  statusPageConfig: {
    showRconPort: true,
    showQueryPort: true,
    showFtp: true,
  },
  readOnlyMode: false,
});
